import { useEffect, useState } from "react";
import { Order } from "../../../entity/Entity";
import {
    GetOrdersByStatus,
    AddOrder,
    GetItemByID,
} from "../../../api/requests/Api";
import MenuAdmin from "../menu/MenuAdmin";
import "./AdminOrders.css";

type OrderStatus = "New" | "Process" | "Finished";

const AdminOrders = () => {
    const [newOrders, setNewOrders] = useState<Order[]>([]);
    const [processOrders, setProcessOrders] = useState<Order[]>([]);
    const [finishedOrders, setFinishedOrders] = useState<Order[]>([]);
    const [itemTitles, setItemTitles] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        loadAllOrders();
        // eslint-disable-next-line
    }, []);

    const loadAllOrders = async () => {
        try {
            setLoading(true);

            const [newData, processData, finishedData] = await Promise.all([
                GetOrdersByStatus("New"),
                GetOrdersByStatus("Process"),
                GetOrdersByStatus("Finished"),
            ]);

            setNewOrders(newData);
            setProcessOrders(processData);
            setFinishedOrders(finishedData);

            const allOrders = [...newData, ...processData, ...finishedData];
            const uniqueItemIds = new Set<string>();

            allOrders.forEach((order) =>
                order.cart.forEach((c) => uniqueItemIds.add(c.item_id))
            );

            uniqueItemIds.forEach((id) => resolveItemTitle(id));
        } catch {
            setMessage("Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    const resolveItemTitle = async (itemId: string) => {
        if (itemTitles[itemId]) return;

        try {
            const data = await GetItemByID(itemId);

            if (data.length > 0) {
                setItemTitles((prev) => ({
                    ...prev,
                    [itemId]: data[0].title,
                }));
            } else {
                setItemTitles((prev) => ({
                    ...prev,
                    [itemId]: "Unknown item",
                }));
            }
        } catch {
            setItemTitles((prev) => ({
                ...prev,
                [itemId]: "Unknown item",
            }));
        }
    };

    const updateOrderStatus = async (order: Order, status: OrderStatus) => {
        try {
            const updatedOrder: Order = {
                ...order,
                status,
            };

            await AddOrder(updatedOrder as any);
            setMessage("Order status updated");
            loadAllOrders();
        } catch {
            setMessage("Failed to update order");
        }
    };

    const renderOrder = (order: Order) => (
        <div key={order._id} className="admin-order-card">
            <div className="admin-order-header">
                <span className="admin-order-id">
                    Order #{order._id}
                </span>
            </div>

            {/* Client Info */}
            <div className="admin-order-client">
                <div>
                    <strong>Name:</strong> {order.client.name}
                </div>
                <div>
                    <strong>Phone:</strong> {order.client.phone_number}
                </div>
                <div>
                    <strong>Email:</strong> {order.client.email}
                </div>
            </div>

            {/* Cart Items */}
            <div className="admin-order-items">
                <strong>Items:</strong>
                {order.cart.map((item, idx) => (
                    <div key={idx} className="admin-order-item">
                        <div>
                            <strong>Item:</strong>{" "}
                            {itemTitles[item.item_id] || "Загрузка..."}
                        </div>
                        <div>
                            <strong>Options:</strong>{" "}
                            {item.changeable_characteristics.join(", ")}
                        </div>
                        <div>
                            <strong>Quantity:</strong> {item.quantity}
                        </div>
                    </div>
                ))}
            </div>

            {/* Status Control */}
            <div className="admin-order-actions">
                <label>Status</label>
                <select
                    value={order.status}
                    onChange={(e) =>
                        updateOrderStatus(
                            order,
                            e.target.value as OrderStatus
                        )
                    }
                >
                    <option value="New">New</option>
                    <option value="Process">Process</option>
                    <option value="Finished">Finished</option>
                </select>
            </div>
        </div>
    );

    if (loading) {
        return <div className="admin-orders-loading">Загрузка...</div>;
    }

    return (
        <div className="admin-orders-page">
            <MenuAdmin />

            <h1 className="admin-orders-title">Orders Management</h1>

            {message && (
                <div className="admin-orders-message">{message}</div>
            )}

            <div className="admin-orders-columns">
                <div className="admin-orders-column">
                    <h2>New</h2>
                    {newOrders.length === 0 && (
                        <div className="admin-orders-empty">No orders</div>
                    )}
                    {newOrders.map(renderOrder)}
                </div>

                <div className="admin-orders-column">
                    <h2>Process</h2>
                    {processOrders.length === 0 && (
                        <div className="admin-orders-empty">No orders</div>
                    )}
                    {processOrders.map(renderOrder)}
                </div>

                <div className="admin-orders-column">
                    <h2>Finished</h2>
                    {finishedOrders.length === 0 && (
                        <div className="admin-orders-empty">No orders</div>
                    )}
                    {finishedOrders.map(renderOrder)}
                </div>
            </div>
        </div>
    );
};

export default AdminOrders;
